// Tako desk box: a wedge-shaped enclosure for the tako hub (ESP32 DevKitC, WeAct 1.54″
// e-paper, 1-channel relay, vibration-motor driver). The e-paper looks out of the tilted
// front; everything else sits on the base plate. The body prints upside down (top on the
// bed, no supports), the base plate and the e-paper retainer print flat.
//
// Coordinates: mm, x = width (left → right), y = depth (front 0 → back), z = up, base at 0.
// Every module size is a parameter — clone boards differ, measure yours.

const rad = (d) => (d * Math.PI) / 180

export const TAKO_PARAMS = [
  { group: 'Gehäuse', key: 'W', label: 'Breite', default: 120, min: 80, max: 220 },
  { group: 'Gehäuse', key: 'D', label: 'Tiefe', default: 90, min: 70, max: 200 },
  { group: 'Gehäuse', key: 'H', label: 'Höhe', default: 62, min: 50, max: 150 },
  { group: 'Gehäuse', key: 'tilt', label: 'Neigung der Front', unit: '°', default: 18, min: 0, max: 35 },
  { group: 'Gehäuse', key: 'wall', label: 'Wandstärke', default: 2.4, min: 1.6, max: 4, step: 0.2 },
  { group: 'Gehäuse', key: 'radius', label: 'Eckenradius', default: 5, min: 0, max: 15 },
  { group: 'Gehäuse', key: 'fit', label: 'Spiel Boden ↔ Gehäuse', default: 0.3, min: 0.1, max: 0.8, step: 0.05 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdW', label: 'Platine Breite', default: 48, min: 30, max: 80, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdH', label: 'Platine Höhe', default: 33, min: 25, max: 60, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdStack', label: 'Glas + Platine dick', default: 2.6, min: 1.5, max: 5, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdView', label: 'Sichtfenster', default: 28, min: 20, max: 40, step: 0.5 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdDx', label: 'Fenster seitlich versetzt', default: 0, min: -15, max: 15, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdDy', label: 'Fenster höher/tiefer', default: 0, min: -10, max: 10, step: 0.1 },
  { group: 'E-Paper (WeAct 1,54″)', key: 'epdPos', label: 'Mitte über Grundfläche (schräg)', default: 35, min: 20, max: 100 },
  { group: 'ESP32 DevKitC', key: 'espL', label: 'Länge', default: 54.4, min: 45, max: 70, step: 0.1 },
  { group: 'ESP32 DevKitC', key: 'espW', label: 'Breite', default: 28, min: 24, max: 32, step: 0.1 },
  { group: 'ESP32 DevKitC', key: 'espLift', label: 'Höhe über Boden (Platz für Jumper)', default: 26, min: 12, max: 35 },
  { group: 'Relais-Modul', key: 'relL', label: 'Länge', default: 50, min: 30, max: 80 },
  { group: 'Relais-Modul', key: 'relW', label: 'Breite', default: 26, min: 15, max: 40 },
  { group: 'Vibrationsmotor-Treiber', key: 'vibL', label: 'Länge', default: 32, min: 15, max: 60 },
  { group: 'Vibrationsmotor-Treiber', key: 'vibW', label: 'Breite', default: 22, min: 10, max: 40 },
]

export const takoDefaults = () => Object.fromEntries(TAKO_PARAMS.map((p) => [p.key, p.default]))

// Hardware the box takes (M3 heat-set inserts: Ø 4.0 hole, 5.7 long, e.g. Ruthex).
const INSERT = { d: 4.0, depth: 6 }
const BASE_T = 3, RIM_H = 3, RIM_T = 1.6, BOSS_R = 4

/**
 * @param M  the manifold-3d module ({ Manifold, CrossSection })
 * @returns { parts: [{ id, name, solid, color, print: Manifold-in-print-pose, place: [x,y,z] offset when exploded }], ghosts, info }
 */
export function buildTako(M, raw = {}) {
  const P = { ...takoDefaults(), ...raw }
  const { Manifold, CrossSection } = M
  const { W, D, H, wall, fit } = P
  const a = rad(P.tilt), ta = Math.tan(a), ca = Math.cos(a)
  const r = Math.min(P.radius, W / 2 - 1, D / 2 - 1)

  // ── helpers ────────────────────────────────────────────────────────────
  const box = (x0, y0, z0, x1, y1, z1) => Manifold.cube([x1 - x0, y1 - y0, z1 - z0]).translate([x0, y0, z0])
  const cyl = (x, y, z0, h, d, seg = 40) => Manifold.cylinder(h, d / 2, d / 2, seg).translate([x, y, z0])
  // Side profile (y, z) extruded across the width x0..x1.
  const profile = (pts, x0, x1) => Manifold.extrude(CrossSection.ofPolygons([pts]), x1 - x0).rotate([90, 0, 90]).translate([x0, 0, 0])
  // Rounded footprint (x, y) as a tall prism z0..z1.
  const foot = (x0, y0, x1, y1, rr, z0, z1) => {
    const cs = rr > 0.2 ? CrossSection.square([x1 - x0 - 2 * rr, y1 - y0 - 2 * rr]).translate([x0 + rr, y0 + rr]).offset(rr, 'Round', 2, 48) : CrossSection.square([x1 - x0, y1 - y0]).translate([x0, y0])
    return Manifold.extrude(cs, z1 - z0).translate([0, 0, z0])
  }
  // Front-wall frame: local x = across, local y = into the box (0 = outer face), local z = up
  // the slope from the bottom edge. Objects built there are tilted onto the wall.
  const onFront = (m) => m.rotate([-P.tilt, 0, 0]).translate([W / 2, 0, 0])
  const lbox = box // boxes in the front-wall frame (see onFront)

  // Inner front face at height z: y = z·tan(a) + wall/cos(a).
  const innerFrontY = (z) => z * ta + wall / ca

  // ── body ───────────────────────────────────────────────────────────────
  const outer = profile([[0, 0], [D, 0], [D, H], [H * ta, H]], 0, W).intersect(foot(0, 0, W, D, r, -1, H + 1))
  const zb = -1 // cavity reaches below the open bottom
  const cavity = profile([[innerFrontY(zb), zb], [D - wall, zb], [D - wall, H - wall], [innerFrontY(H - wall), H - wall]], wall, W - wall)
    .intersect(foot(wall, -5, W - wall, D - wall, Math.max(0.5, r - wall), zb, H))
  let body = outer.subtract(cavity)

  // Corner bosses with heat-set inserts from below, for the base-plate screws. They are
  // clipped to the inside, so the front ones merge into the sloped wall as it closes in.
  const bossAt = [
    [wall + BOSS_R + 0.6, innerFrontY(8) + BOSS_R + 0.6],
    [W - wall - BOSS_R - 0.6, innerFrontY(8) + BOSS_R + 0.6],
    [wall + BOSS_R + 0.6, D - wall - BOSS_R - 0.6],
    [W - wall - BOSS_R - 0.6, D - wall - BOSS_R - 0.6],
  ]
  body = body.add(Manifold.union(bossAt.map(([x, y]) => cyl(x, y, 0, H, BOSS_R * 2))).intersect(outer))
  body = body.subtract(Manifold.union(bossAt.map(([x, y]) => cyl(x, y, -1, INSERT.depth + 1, INSERT.d))))

  // E-paper: window with a chamfer outside, corner guides and two retainer bosses inside.
  const sPcb = P.epdPos // slope distance of the PCB centre from the bottom edge
  const winX = P.epdDx, winZ = sPcb + P.epdDy
  const v = P.epdView, ch = Math.min(1.4, wall * 0.6)
  const windowCut = Manifold.union([
    lbox(winX - v / 2, -1, winZ - v / 2, winX + v / 2, wall + 1, winZ + v / 2),
    // 45° chamfer: a frustum from outside the face (1 mm out, so no face lies on the wall
    // surface) to depth ch, where it meets the window.
    Manifold.hull([lbox(winX - v / 2 - ch - 1, -1.01, winZ - v / 2 - ch - 1, winX + v / 2 + ch + 1, -1, winZ + v / 2 + ch + 1), lbox(winX - v / 2, ch, winZ - v / 2, winX + v / 2, ch + 0.01, winZ + v / 2)]),
  ])
  body = body.subtract(onFront(windowCut))
  const pw = P.epdW + 0.6, ph = P.epdH + 0.6, gt = 1.2, gh = P.epdStack + 0.8, leg = 7
  const guides = []
  const span = (p, q) => [Math.min(p, q), Math.max(p, q)]
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    // L-shaped guide around each PCB corner, just outside the outline: one leg along the
    // width, one up the slope; the sides stay open for the wires.
    const cx = sx * (pw / 2), cz = sPcb + sz * (ph / 2)
    const [ax0, ax1] = span(cx - sx * leg, cx + sx * gt), [az0, az1] = span(cz, cz + sz * gt)
    const [bx0, bx1] = span(cx, cx + sx * gt), [bz0, bz1] = span(cz - sz * leg, cz + sz * gt)
    guides.push(lbox(ax0, wall - 0.2, az0, ax1, wall + gh, az1), lbox(bx0, wall - 0.2, bz0, bx1, wall + gh, bz1))
  }
  const retX = pw / 2 + 5.5, retH = 8
  const retBoss = Manifold.union([-1, 1].map((sx) => Manifold.cylinder(retH + 0.2, BOSS_R, BOSS_R, 40).rotate([-90, 0, 0]).translate([sx * retX, wall - 0.2, sPcb])))
  const retHoles = Manifold.union([-1, 1].map((sx) => Manifold.cylinder(INSERT.depth + 1, INSERT.d / 2, INSERT.d / 2, 32).rotate([90, 0, 0]).translate([sx * retX, wall + retH + 1, sPcb])))
  body = body.add(onFront(Manifold.union([...guides, retBoss])).intersect(outer)).subtract(onFront(retHoles))

  // Back wall: USB notch (open to the bottom, so the body lowers over the plugged cable),
  // a slot for the relay's wires, vent slots up high.
  const esp = { x: wall + 2 * BOSS_R + 4 + P.espW / 2, y1: D - wall - 3, lift: P.espLift } // clear of the corner bosses
  esp.y0 = esp.y1 - P.espL
  const usbTop = esp.lift + 1.6 + 9
  const relay = { x1: W - wall - 6, y1: D - wall - 2 * BOSS_R - 4 } // clear of the back corner boss
  relay.x0 = relay.x1 - P.relW; relay.y0 = relay.y1 - P.relL
  const notch = (cx, w, h) => Manifold.union([box(cx - w / 2, D - wall - 1, -1, cx + w / 2, D + 1, h - w / 2), Manifold.cylinder(wall + 2, w / 2, w / 2, 40).rotate([-90, 0, 0]).translate([cx, D - wall - 1, h - w / 2])])
  body = body.subtract(notch(esp.x, 13, usbTop)).subtract(notch((relay.x0 + relay.x1) / 2, 9, 12))
  const vents = []
  for (let i = 0; i < 5; i++) vents.push(box(W / 2 - 16 + i * 8 - 1, D - wall - 1, H - 26, W / 2 - 16 + i * 8 + 1, D + 1, H - 8))
  body = body.subtract(Manifold.union(vents))

  // ── base plate ─────────────────────────────────────────────────────────
  // Plate under the body (z -BASE_T..0), a rim that keys into the body's opening, the
  // ESP32 bridge, trays for relay and motor driver, countersunk screw holes, foot recesses.
  let base = foot(0, 0, W, D, r, -BASE_T, 0)
  const rimY0 = innerFrontY(RIM_H) + fit // the sloped front closes in: key to the top of the rim
  const rimOuter = foot(wall + fit, rimY0, W - wall - fit, D - wall - fit, Math.max(0.5, r - wall - fit), 0, RIM_H)
  const rimInner = foot(wall + fit + RIM_T, rimY0 + RIM_T, W - wall - fit - RIM_T, D - wall - fit - RIM_T, Math.max(0.5, r - wall - fit - RIM_T), -0.5, RIM_H + 1)
  let rim = rimOuter.subtract(rimInner).subtract(Manifold.union(bossAt.map(([x, y]) => cyl(x, y, -0.5, RIM_H + 1, BOSS_R * 2 + 1.2))))
  base = base.add(rim)
  // ESP32 bridge: two blocks under the board's ends, between the header rows (the pins hang
  // down between them with their jumper plugs). Antenna end: a stop lip and a zip-tie slot.
  const bw = Math.max(10, P.espW - 9) // between the header rows
  const block = (y0, y1) => box(esp.x - bw / 2, y0, 0, esp.x + bw / 2, y1, esp.lift)
  let bridge = Manifold.union([block(esp.y0 - 2, esp.y0 + 5), block(esp.y1 - 5, esp.y1)])
  bridge = bridge.add(box(esp.x - bw / 2, esp.y0 - 4, 0, esp.x + bw / 2, esp.y0 - 2, esp.lift + 1.6 + 2.5)) // stop lip
  bridge = bridge.subtract(box(esp.x - bw / 2 - 1, esp.y0 + 0.5, esp.lift - 7, esp.x + bw / 2 + 1, esp.y0 + 3.5, esp.lift - 5)) // zip-tie slot
  base = base.add(bridge)
  // Trays (tape inside): low walls with a gap for the wires.
  const tray = (x0, y0, x1, y1, gapSide) => {
    const o = box(x0 - 1.8, y0 - 1.8, 0, x1 + 1.8, y1 + 1.8, 4), i = box(x0 - 0.3, y0 - 0.3, -1, x1 + 0.3, y1 + 0.3, 5)
    const cx = (x0 + x1) / 2
    const gap = gapSide === 'back' ? box(cx - 9, y1 - 1, -1, cx + 9, y1 + 3, 5) : box(cx - 9, y0 - 3, -1, cx + 9, y0 + 1, 5)
    return o.subtract(i).subtract(gap)
  }
  base = base.add(tray(relay.x0, relay.y0, relay.x1, relay.y1, 'back'))
  // Motor driver at the front, under the e-paper: it is low, and its vibration reaches the
  // desk right below the display.
  const vib = { x0: W / 2 - P.vibL / 2, y0: innerFrontY(12) + 3 }
  vib.x1 = vib.x0 + P.vibL; vib.y1 = vib.y0 + P.vibW
  base = base.add(tray(vib.x0, vib.y0, vib.x1, vib.y1, 'back'))
  // Screws from below: countersunk M3 (head Ø 6, 90°).
  const holes = bossAt.map(([x, y]) => Manifold.union([cyl(x, y, -BASE_T - 1, BASE_T + RIM_H + 2, 3.4), Manifold.cylinder(1.7, 3.3, 1.7, 40).translate([x, y, -BASE_T - 0.01])]))
  base = base.subtract(Manifold.union(holes))
  const feet = [[12, 12], [W - 12, 12], [12, D - 12], [W - 12, D - 12]].map(([x, y]) => cyl(x, y, -BASE_T - 1, 2, 10.4))
  base = base.subtract(Manifold.union(feet))

  // ── e-paper retainer: a bar over the module's back, pressing on the PCB's side edges ──
  const barL = retX * 2 + 8, barW = 8, barT = 3, padH = retH - P.epdStack
  let retainer = box(-barL / 2, -barW / 2, 0, barL / 2, barW / 2, barT)
  for (const sx of [-1, 1]) retainer = retainer.add(box(sx * (pw / 2 - 4) - 3, -barW / 2, -padH, sx * (pw / 2 - 4) + 3, barW / 2, 0)) // on the PCB's side strips
  retainer = retainer.subtract(Manifold.union([-1, 1].map((sx) => cyl(sx * retX, 0, -padH - 1, barT + padH + 2, 3.4))))
  // In the box: bar lies on the bosses, i.e. at local y = wall + retH (+ its thickness).
  // Its thickness turns to point into the box, pads towards the wall.
  const retPlaced = onFront(retainer.rotate([-90, 0, 0]).translate([0, wall + retH, sPcb]))
  // Print poses: body upside down on its top, plate as is (bottom down), retainer flat.
  const toBed = (m) => { const b = m.boundingBox(); return m.translate([-b.min[0], -b.min[1], -b.min[2]]) }

  // Stand-ins for the modules (not printed): to see the fit.
  const ghosts = [
    { name: 'ESP32 DevKitC', solid: box(esp.x - P.espW / 2, esp.y0, esp.lift, esp.x + P.espW / 2, esp.y1, esp.lift + 1.6).add(box(esp.x - 9, esp.y0, esp.lift + 1.6, esp.x + 9, esp.y0 + 25.5, esp.lift + 4.7)) },
    { name: 'Relais', solid: box(relay.x0, relay.y0, 0.5, relay.x1, relay.y1, 19) },
    { name: 'Vibrationstreiber', solid: box(vib.x0, vib.y0, 0.5, vib.x1, vib.y1, 10) },
    { name: 'E-Paper', solid: onFront(box(-P.epdW / 2, wall, sPcb - P.epdH / 2, P.epdW / 2, wall + P.epdStack, sPcb + P.epdH / 2)) },
  ]

  const parts = [
    { id: 'gehaeuse', name: 'Gehäuse', solid: body, color: '#d8d4cc', print: toBed(body.rotate([180, 0, 0])), explode: [0, 0, 45], note: 'kopfüber drucken (Oberseite aufs Bett), ohne Stützen' },
    { id: 'boden', name: 'Bodenplatte', solid: base, color: '#8fa38a', print: toBed(base), explode: [0, 0, -25], note: 'flach, Unterseite aufs Bett' },
    { id: 'halter', name: 'E-Paper-Halter', solid: retPlaced, color: '#c69a6a', print: toBed(retainer.rotate([180, 0, 0])), explode: [0, 30 * Math.cos(a), -30 * Math.sin(a)], note: 'flach drucken' },
  ]
  const issues = []
  if (relay.x0 < esp.x + P.espW / 2 + 4) issues.push('Relais und ESP32 überschneiden sich — Gehäuse breiter machen.')
  const overlap = (p, q) => p.x0 < q.x1 && q.x0 < p.x1 && p.y0 < q.y1 && q.y0 < p.y1
  // The board itself sits high above the trays; on the floor only its support blocks count.
  const espBox = { x0: esp.x - bw / 2, x1: esp.x + bw / 2, y0: esp.y0 - 4, y1: esp.y1 }
  const grow = (b) => ({ x0: b.x0 - 2, x1: b.x1 + 2, y0: b.y0 - 2, y1: b.y1 + 2 })
  if (overlap(grow(vib), espBox) || overlap(grow(vib), grow(relay))) issues.push('Vibrationstreiber stößt an ESP32 oder Relais — Gehäuse breiter oder tiefer machen.')
  if (vib.x0 < wall + 2 || vib.x1 > W - wall - 2) issues.push('Vibrationstreiber ist breiter als das Gehäuse.')
  if (sPcb + P.epdH / 2 + 4 > (H - wall) / ca) issues.push('E-Paper ragt oben in den Deckel — tiefer setzen oder Gehäuse höher.')
  if (esp.y0 - 4 < innerFrontY(esp.lift + 3)) issues.push('ESP32 stößt vorn an die schräge Front — Gehäuse tiefer machen.')
  return { parts, ghosts, issues, params: P, size: [W, D, H + BASE_T] }
}
